import { useCustom } from '@refinedev/core';
import { Table, Card, Tag, Space, Select, DatePicker, Typography, Avatar, Badge } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { t } from '../../i18n';

const { RangePicker } = DatePicker;
const { Text } = Typography;

interface AuditLog {
  id: number;
  staffId: number;
  action: string;
  entity: string;
  entityId: number | null;
  oldValue: Record<string, unknown> | null;
  newValue: Record<string, unknown> | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
  staff: {
    id: number;
    username: string;
    fullName: string | null;
  };
  // Backend collapses audit rows that share
  // (staffId, action, entity, entityId) within the same second into
  // one representative row. >1 means this entry covers N raw audits.
  groupCount?: number;
}

interface ActivityLogListProps {
  staffId?: number;
  embedded?: boolean;
  title?: string;
}

const actionLabels: Record<string, string> = {
  'staff.create': t('audit.staffCreated'),
  'staff.update': t('audit.staffUpdated'),
  'staff.delete': t('audit.staffCreated'),
  'user.block': t('users.userBlocked'),
  'user.unblock': t('users.userUnblocked'),
  'user.update': t('users.userEdited'),
  'driver.verify': t('drivers.driverVerified'),
  'driver.reject': t('drivers.driverRejected'),
  'driver.create': 'Haydovchi yaratildi',
  'document.approve': 'Hujjat tasdiqlandi',
  'document.reject': 'Hujjat rad etildi',
  'trip.create': 'Sayohat yaratildi',
  'trip.cancel': 'Sayohat bekor qilindi',
  'ticket.reply': t('audit.ticketReplied'),
  'ticket.resolve': t('audit.ticketResolved'),
  'faq.create': t('audit.faqCreated'),
  'faq.update': t('audit.faqUpdated'),
  'faq.delete': t('audit.faqCreated'),
  'promo.create': t('audit.promoCreated'),
  'promo.update': t('audit.promoCreated'),
  'promo.toggle': t('audit.promoCreated'),
  'settings.update': t('audit.settingsUpdated'),
  'wallet.topup': 'Hamyon to\'ldirildi',
  'wallet.deduct': 'Hamyondan yechildi',
};

const entityLabels: Record<string, string> = {
  Staff: t('staff.title'),
  User: t('users.title'),
  Driver: t('drivers.title'),
  Document: t('documents.title'),
  Trip: t('trips.title'),
  Ticket: t('support.title'),
  FAQ: 'FAQ',
  PromoCode: t('promo.title'),
  Settings: t('settings.title'),
  Wallet: 'Hamyon',
};

const actionColors: Record<string, string> = {
  create: 'green',
  update: 'blue',
  delete: 'red',
  verify: 'green',
  reject: 'red',
  approve: 'green',
  block: 'red',
  unblock: 'green',
  cancel: 'orange',
  reply: 'blue',
  resolve: 'green',
  toggle: 'orange',
  topup: 'green',
  deduct: 'red',
};

export const ActivityLogList = ({ staffId, embedded = false, title = 'Faoliyat tarixi' }: ActivityLogListProps) => {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [action, setAction] = useState<string | undefined>();
  const [entity, setEntity] = useState<string | undefined>();
  const [filterStaffId, setFilterStaffId] = useState<number | undefined>();
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);

  // Resolve the staff-id we actually send to the backend. If the page is
  // embedded inside a specific staff member's profile, that id wins and
  // the per-page staff filter is hidden. Otherwise use the dropdown.
  const effectiveStaffId = staffId ?? filterStaffId;

  // Reset to page 1 whenever a filter changes so we don't land on a
  // stale empty page (previous version kept the page index, which made
  // the filter look broken).
  useEffect(() => {
    setPage(1);
  }, [action, entity, filterStaffId, dateRange]);

  const { data, isLoading } = useCustom<{ data: AuditLog[]; meta: { total: number; rawTotal?: number } }>({
    url: '/admin/audit-logs',
    method: 'get',
    config: {
      query: {
        page,
        limit,
        staffId: effectiveStaffId || undefined,
        action: action || undefined,
        entity: entity || undefined,
        startDate: dateRange?.[0] || undefined,
        endDate: dateRange?.[1] || undefined,
      },
    },
  });

  // Staff dropdown options (skipped when embedded inside a staff profile).
  const { data: staffData } = useCustom<{ data: { id: number; username: string; fullName: string | null }[] }>({
    url: '/admin/staff',
    method: 'get',
    config: { query: { limit: 200 } },
    queryOptions: { enabled: !staffId },
  });
  const staffOptions = (staffData?.data?.data || []).map((s) => ({
    value: s.id,
    label: s.fullName || s.username,
  }));

  const logs = data?.data?.data || [];
  // `total` (grouped) drives pagination; `rawTotal` (sum of the
  // underlying audit_log rows) drives the "Topildi" badge so the
  // operator sees every event the filter matched — including the
  // ones that visually collapsed into a single grouped row.
  const total = data?.data?.meta?.total || 0;
  const rawTotal = data?.data?.meta?.rawTotal ?? total;
  const filtersActive = !!(action || entity || filterStaffId || dateRange);

  const getActionColor = (action: string) => {
    const parts = action.split('.');
    const actionType = parts[parts.length - 1];
    return actionColors[actionType] || 'default';
  };

  const columns = [
    {
      title: t('common.date'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (date: string) => new Date(date).toLocaleString('uz-UZ'),
    },
    ...(staffId
      ? []
      : [
          {
            title: t('staff.title'),
            dataIndex: 'staff',
            key: 'staff',
            width: 180,
            render: (staff: AuditLog['staff']) => (
              <Space>
                <Avatar size="small" icon={<UserOutlined />} />
                <Text>{staff?.fullName || staff?.username}</Text>
              </Space>
            ),
          },
        ]),
    {
      title: t('common.actions'),
      dataIndex: 'action',
      key: 'action',
      width: 240,
      render: (action: string, record: AuditLog) => (
        <Space size={6} wrap>
          <Tag color={getActionColor(action)}>
            {actionLabels[action] || action}
          </Tag>
          {/* × N badge — the row represents N raw audit entries that
              share (staffId, action, entity, entityId, second). The
              "Topildi" badge above the table still reflects the raw
              event count via meta.rawTotal. */}
          {record.groupCount && record.groupCount > 1 && (
            <Tag color="geekblue" style={{ margin: 0 }}>
              × {record.groupCount}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: t('common.type'),
      key: 'entity',
      width: 150,
      render: (_: unknown, record: AuditLog) => (
        <Space>
          <Text>{entityLabels[record.entity] || record.entity}</Text>
          {record.entityId && <Text type="secondary">#{record.entityId}</Text>}
        </Space>
      ),
    },
    {
      title: 'Tafsilotlar',
      key: 'details',
      width: 250,
      render: (_: unknown, record: AuditLog) => {
        if (record.entity === 'Wallet' && record.newValue) {
          const nv = record.newValue as Record<string, unknown>;
          const ov = record.oldValue as Record<string, unknown>;
          const isTopup = record.action === 'wallet.topup';
          return (
            <Space direction="vertical" size={0}>
              <Text type={isTopup ? undefined : 'danger'}>
                {isTopup ? '+' : '-'}{String(nv.amount || '')} so'm
              </Text>
              {nv.reason && <Text type="secondary" style={{ fontSize: 12 }}>{String(nv.reason)}</Text>}
              <Text type="secondary" style={{ fontSize: 11 }}>
                Balans: {String(ov?.walletBalance || 0)} → {String(nv.walletBalance || 0)}
              </Text>
            </Space>
          );
        }
        return null;
      },
    },
    {
      title: 'IP manzil',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      width: 130,
      render: (ip: string | null) => ip || '-',
    },
  ];

  const content = (
    <>
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        {!staffId && (
          <Select
            placeholder={t('staff.title')}
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 220 }}
            value={filterStaffId}
            onChange={(val) => setFilterStaffId(val)}
            options={staffOptions}
          />
        )}
        <Select
          placeholder={t('common.actions')}
          allowClear
          style={{ width: 200 }}
          value={action}
          onChange={setAction}
          options={Object.entries(actionLabels).map(([value, label]) => ({
            value,
            label,
          }))}
        />
        <Select
          placeholder={t('common.type')}
          allowClear
          style={{ width: 150 }}
          value={entity}
          onChange={setEntity}
          options={Object.entries(entityLabels).map(([value, label]) => ({
            value,
            label,
          }))}
        />
        <RangePicker
          placeholder={[t('promo.startDate'), t('promo.endDate')]}
          onChange={(dates) => {
            if (dates && dates[0] && dates[1]) {
              setDateRange([
                dates[0].format('YYYY-MM-DD'),
                dates[1].format('YYYY-MM-DD'),
              ]);
            } else {
              setDateRange(null);
            }
          }}
        />
        <Badge
          count={rawTotal}
          overflowCount={99999}
          showZero
          style={{
            backgroundColor: filtersActive ? '#1890ff' : '#95A5A6',
            fontWeight: 600,
          }}
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          {filtersActive ? t('common.filtered') : t('common.total')}
        </Text>
      </div>

      <Table
        dataSource={logs}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        size="small"
        pagination={{
          current: page,
          total,
          pageSize: limit,
          onChange: setPage,
          showSizeChanger: false,
          showTotal: (total) => `${t('common.total')}: ${total}`,
        }}
      />
    </>
  );

  if (embedded) {
    return <Card title={title}>{content}</Card>;
  }

  return (
    <div className="p-6">
      <Card title={title}>{content}</Card>
    </div>
  );
};
